import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Dropdown, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message,
  Card,
  Typography,
  Row,
  Col,
  Divider,
  Menu,
  Empty,
  Tooltip,
  Badge,
  Drawer,
  Checkbox
} from 'antd';
import { 
  PlusOutlined, 
  MoreOutlined,
  StopOutlined,
  UserOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { fetchClients, onboardClient, deactivateClient, setClientSearch, deleteClient } from '../store/slices/clientSlice';
import MainLayout from './MainLayout';
import { TIMEZONE_OPTIONS } from '../constants';
import SearchBar from './Atom/SearchBar';
import ClientUsage from './ClientUsage';

const { Title } = Typography;

// Helper function for date formatting
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    return 'Invalid Date';
  }
};

const ClientDashboard = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [drawerId, setDrawerId] = useState('');
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { clients, loading, onboardLoading, search } = useSelector(state => state.client);
  const { profile } = useSelector(state => state.user);
  const [deactivatingClient, setDeactivatingClient] = useState(null);
  
  useEffect(() => {
    dispatch(fetchClients({ search }));
  }, [search, dispatch]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleAdminEmailChange = (e) => {
    const adminEmail = e.target.value;
    form.setFieldsValue({ email: adminEmail });
  };

  const handleAppNameChange = (e) => {
    const appName = e.target.value;
    form.setFieldsValue({ appName });
  };

  const handleFeaturesChange = (feature) => {
    const features = form.getFieldValue('features') || [];
    let checkedValues = [];
    if (features.includes(feature)) {
      // Remove feature
      checkedValues = features.filter(f => f !== feature);
    } else {
      // Add feature
      checkedValues = [...features, feature];
    }
    
    form.setFieldsValue({ features: checkedValues });
  };

  const handleSubmit = async (values) => {
    /**
     * adminEmail, userInformation, companyInformation = {}, onboardedByUser = '', userName = '',
    identityId = '', appName, database: { dbString, dbName },
     */

    try {
      const payload = {
        adminEmail: values.adminEmail,
        companyName: values.companyName,
        timezone: values.timezone,
        appName: values.appName,
        userInformation: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.adminEmail
        },
        database: {
          dbName: values.dbName,
          dbString: values.dbString
        },
        address: {
          addressLine1: values.addressLine1,
          city: values.city,
          state: values.state,
          pincode: values.pincode,
          country: values.country
        },
        features: values.features,
        onboardedByUser: profile.email,
      };
      await dispatch(onboardClient(payload)).unwrap();
      
      message.success('Client onboarded successfully!');
      setIsModalVisible(false);
      form.resetFields();
      
      // Refresh client list
      dispatch(fetchClients({ search }));
    } catch (error) {
      message.error(error.message || 'Failed to onboard client');
    }
  };

  const handleAction = async (action, record) => {
    switch (action) {
      case 'edit':
        // Handle edit action
        console.log('Edit client:', record);
        break;
      case 'deactivate':
        try {
          setDeactivatingClient(record.id);
          await dispatch(deactivateClient(record.id));
          message.success('Client deactivated successfully!');
          dispatch(fetchClients({ search }));
        } catch (error) {
          message.error(error.message || 'Failed to deactivate client');
        } finally {
          setDeactivatingClient(null);
        }
        break;
      case 'delete':
        try {
          setDeactivatingClient(record.id);
          await dispatch(deleteClient(record.id));
          message.success('Client deleted successfully!');
          dispatch(fetchClients({ search }));
        } catch (error) {
          message.error(error.message || 'Failed to delete client');
        } finally {
          setDeactivatingClient(null);
        }
        break;
      case 'viewDashboard':
        // Handle view dashboard action
        console.log('View dashboard:', record);
        break;
      default:
        break;
    }
  };

  const columns = [
    {
        title: 'Account Id',
        dataIndex: '_id',
        key: '_id',
        render: (text, record) =>
        <div className='accountId-section'>
        <Badge.Ribbon text={record.status === 'in-active' ? 'In-Active' : 'Active'} color={record.status === 'in-active' ? 'red' : 'green'}>
          <code style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '150px' }}>
              {text}
          </code>
        </Badge.Ribbon>
        </div>
      },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (text, record) => <p>
          <strong>{text}</strong>
          {text && <br />}
          <code>{record.adminEmail}</code>
        </p>
    },
    {
      title: 'License Key',
      dataIndex: 'sdkUrl',
      key: 'sdkUrl',
      render: (text = '') => (
        <Tooltip title="Copy">
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(text).then(() => {
                message.success('Copied to clipboard!');
              });
            }}
            type='text'
          >
            <code style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '300px' }}>
              {text}
            </code>
          </Button>
        </Tooltip>
      ),
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu
              items={[
                {
                  key: 'deactivate',
                  icon: <StopOutlined />,
                  label: record.status === 'in-active' ? 'Activate' : 'Deactivate',
                  onClick: () => {
                    handleAction('deactivate', record)
                  },
                  disabled: deactivatingClient === record.id,
                },
                // Dev Note: Hiding delete for now
                // {
                //   key: 'delete',
                //   icon: <DeleteOutlined />,
                //   label: 'Delete Client',
                //   onClick: () => handleAction('delete', record),
                // },
              ]}
            />
          }
          trigger={['click']}
        >
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            loading={deactivatingClient === record.id}
            onClick={(e) => e.stopPropagation()}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <MainLayout pageTitle="Client Management">
      <div>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col span={10}>
            <SearchBar wait={600} onSearch={(val) => dispatch(setClientSearch(val))} placeholder='Search by Company Name, accountId' />
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />} 
                onClick={() => dispatch(fetchClients({ search }))}
                loading={loading}
              >
                Refresh
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={showModal}
              >
                Onboard Client
              </Button>
            </Space>
          </Col>
        </Row>

          <Table
            columns={columns}
            dataSource={clients}
            loading={loading}
            rowKey="id"
            rowClassName={(record) => record.status === 'in-active' ? 'row-inactive' : ''}
            onRow={(record) => ({
              onClick: () => setDrawerId(record._id),
              style: { cursor: "pointer" }
            })}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No clients found"
                />
              )
            }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} clients`,
            }}
          />

          <Drawer
            title={`Client Usage`}
            width={900}
            placement="right"
            open={!!drawerId}
            onClose={() => setDrawerId('')}
          >
            <ClientUsage accountId={drawerId} />
          </Drawer>
        <Modal
          title="Onboard New Client"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={900}
          style={{ top: '32px' }}
          destroyOnClose
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              email: form.getFieldValue('adminEmail'),
              features: []
            }}
          >
            {/* Basic Information */}
            <Col span={24}>
            <Form.Item
                  name="appName"
                  label="App Name"
                  rules={[
                    { required: true, message: 'Please input Application name!' },
                  ]}
                >
                  <Input 
                    placeholder="identxlabs" 
                    onChange={handleAppNameChange}
                  />
                </Form.Item>
            </Col>
            <Title level={4} style={{ marginBottom: '16px' }}>
              <UserOutlined style={{ marginRight: '8px' }} />
              Basic Information
            </Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="adminEmail"
                  label="Admin Email"
                  rules={[
                    { required: true, message: 'Please input admin email!' },
                    { type: 'email', message: 'Please enter a valid email!' }
                  ]}
                >
                  <Input 
                    placeholder="admin@company.com" 
                    onChange={handleAdminEmailChange}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="companyName"
                  label="Company Name"
                  rules={[{ required: true, message: 'Please input company name!' }]}
                >
                  <Input placeholder="Company Name" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="timezone"
                  label="Timezone"
                  rules={[{ required: true, message: 'Please select timezone!' }]}
                >
                  <Select placeholder="Select timezone" showSearch options={TIMEZONE_OPTIONS}>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* User Details */}
            <Title level={4} style={{ marginBottom: '16px' }}>
              <UserOutlined style={{ marginRight: '8px' }} />
              User Details
            </Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: 'Please input first name!' }]}
                >
                  <Input placeholder="First Name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: 'Please input last name!' }]}
                >
                  <Input placeholder="Last Name" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Database Details */}
            <Title level={4} style={{ marginBottom: '16px' }}>
              <DatabaseOutlined style={{ marginRight: '8px' }} />
              Database Details
            </Title>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="dbName"
                  label="Database Name"
                  rules={[{ required: true, message: 'Please input database name!' }]}
                >
                  <Input placeholder="Database Name" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="dbString"
                  label="Database Connection String"
                  rules={[{ required: true, message: 'Please input database connection string!' }]}
                >
                  <Input placeholder="mongodb+srv://user:password@db.mongodb.net/" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />
            
            <Title level={4} style={{ marginBottom: '16px' }}>
              <DatabaseOutlined style={{ marginRight: '8px' }} />
              Select Features
            </Title>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="features"
                  label="Features:"
                  rules={[
                    { required: true, validator: (_, value) => {
                      if (value && value.length > 0) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Please select at least one feature!'));
                    }
                  }
                  ]}
                >
                  {
                    ['RASP',
    'BUILD_SECURITY',
    'RISK_CONFIG',
    'BROWSER_FINGERPRINT'].map(feature => (
                      <Checkbox key={feature} value={feature} style={{ lineHeight: '32px' }} onChange={() => handleFeaturesChange(feature)}>
                        {feature}
                      </Checkbox>
                    ))
                  }
                </Form.Item>
              </Col>
            </Row>

            <Divider />


            {/* Address Details */}
            <Title level={4} style={{ marginBottom: '16px' }}>
              <EnvironmentOutlined style={{ marginRight: '8px' }} />
              Address Details (Optional)
            </Title>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name="addressLine1"
                  label="Address Line 1"
                >
                  <Input placeholder="Street Address" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="city"
                  label="City"
                >
                  <Input placeholder="City" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="state"
                  label="State/Province"
                >
                  <Input placeholder="State" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="pincode"
                  label="Postal Code"
                >
                  <Input placeholder="Postal Code" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="country"
                  label="Country"
                >
                  <Input placeholder="Country" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={handleCancel}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={onboardLoading}>
                  Onboard Client
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ClientDashboard;
